import { FieldOption, LogicalOperator, OperatorOption } from '../constants';
import {
  expandAnimalIdentChip,
  expandWildcardPattern,
  isEdgeOnlyWildcardPattern,
  normalizeWildcardInput,
  type AnimalIdentChipExpansion,
} from './animalIdentChipFilter';

export type GqlFilterRow = {
  field: string;
  operator: string;
  value: string;
};

export type GqlFilterSubGroup = {
  filters: GqlFilterRow[];
  groups?: GqlFilterSubGroup[];
  logicalOperator: string;
};

export const IdentChipGqlField = {
  animal: FieldOption.searchTerm.value,
  animalIdent: FieldOption.animalIdent.value,
  animalName: FieldOption.animalName.value,
  breeder: FieldOption.breeder.value,
  breederIdent: 'BREEDER_IDENT',
  currentOwner: FieldOption.currentOwner.value,
  currentOwnerIdent: 'CURRENT_OWNER_IDENT',
  dam: FieldOption.dam.value,
  damIdent: 'DAM_IDENT',
  member: FieldOption.member.value,
  memberIdent: FieldOption.memberIdent.value,
  memberName: 'MEMBER_NAME',
  owner: 'OWNER',
  prefix: FieldOption.herdPrefix.value,
  sire: FieldOption.sire.value,
  sireIdent: 'SIRE_IDENT',
} as const;

const COMPOSITE_BRANCH_FIELDS: Record<string, string[]> = {
  [IdentChipGqlField.animal]: [
    IdentChipGqlField.animalIdent,
    IdentChipGqlField.animalName,
  ],
  [IdentChipGqlField.sire]: [IdentChipGqlField.sireIdent],
  [IdentChipGqlField.dam]: [IdentChipGqlField.damIdent],
  [IdentChipGqlField.breeder]: [IdentChipGqlField.breederIdent],
  [IdentChipGqlField.currentOwner]: [IdentChipGqlField.currentOwnerIdent],
  [IdentChipGqlField.owner]: [IdentChipGqlField.currentOwnerIdent],
  [IdentChipGqlField.member]: [
    IdentChipGqlField.memberIdent,
    IdentChipGqlField.memberName,
    IdentChipGqlField.prefix,
  ],
};

function getBranchFields(gqlCompositeField: string): string[] | null {
  return COMPOSITE_BRANCH_FIELDS[gqlCompositeField.toUpperCase()] ?? null;
}

export type GqlCompositeBranchResolver = (
  gqlCompositeField: string,
) => string[] | null;

function constraintsToAndGroup(
  branchField: string,
  constraints: AnimalIdentChipExpansion[],
): GqlFilterSubGroup {
  return {
    filters: constraints.map((c) => ({
      field: branchField,
      operator: c.operator,
      value: c.value,
    })),
    logicalOperator: LogicalOperator.and,
  };
}

type SingleChipResult =
  | { kind: 'row'; row: GqlFilterRow }
  | { kind: 'andGroup'; group: GqlFilterSubGroup }
  | { kind: 'orBranches'; branches: GqlFilterSubGroup[] };

function processSingleChip(
  chip: string,
  gqlCompositeField: string,
  resolveBranchFields: GqlCompositeBranchResolver,
): SingleChipResult | null {
  const normalized = normalizeWildcardInput(chip).trim();
  if (!normalized) return null;

  if (!normalized.includes('%')) {
    return {
      kind: 'row',
      row: {
        field: gqlCompositeField,
        operator: OperatorOption.equals.value,
        value: normalized,
      },
    };
  }

  if (isEdgeOnlyWildcardPattern(normalized)) {
    const exp = expandAnimalIdentChip(normalized);
    if (!exp.value) return null;
    return {
      kind: 'row',
      row: {
        field: gqlCompositeField,
        operator: exp.operator,
        value: exp.value,
      },
    };
  }

  const constraints = expandWildcardPattern(normalized);
  if (constraints.length === 0) return null;

  const branches = resolveBranchFields(gqlCompositeField);
  if (!branches || branches.length <= 1) {
    const branch = branches?.[0] ?? gqlCompositeField;
    if (constraints.length === 1) {
      return {
        kind: 'row',
        row: { field: branch, ...constraints[0]! },
      };
    }
    return {
      kind: 'andGroup',
      group: constraintsToAndGroup(branch, constraints),
    };
  }

  return {
    kind: 'orBranches',
    branches: branches.map((b) => constraintsToAndGroup(b, constraints)),
  };
}

function chipResultToSubGroup(result: SingleChipResult): GqlFilterSubGroup {
  if (result.kind === 'row') {
    return {
      filters: [result.row],
      logicalOperator: LogicalOperator.and,
    };
  }
  if (result.kind === 'andGroup') {
    return result.group;
  }
  return {
    filters: [],
    groups: result.branches,
    logicalOperator: LogicalOperator.or,
  };
}

/**
 * Converts a chip-style filter row (`operator: ''`, comma-separated / `%`
 * values) into GraphQL filter rows and optional OR/AND sub-groups.
 */
export function processIdentChipFilterToGql(
  compositeField: string,
  value: string,
  transformField: (f: string) => string = (f) => f,
  resolveBranchFields: GqlCompositeBranchResolver = getBranchFields,
): { processed: GqlFilterRow[]; subGroups: GqlFilterSubGroup[] } {
  const chips = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (chips.length === 0) {
    return { processed: [], subGroups: [] };
  }

  const gqlField = transformField(compositeField);
  const results = chips
    .map((chip) => processSingleChip(chip, gqlField, resolveBranchFields))
    .filter((r): r is SingleChipResult => r != null);

  if (results.length === 0) {
    return { processed: [], subGroups: [] };
  }

  const subGroups = results.map(chipResultToSubGroup);

  if (subGroups.length === 1) {
    const g = subGroups[0]!;
    if (g.filters.length === 1 && !g.groups) {
      return { processed: g.filters, subGroups: [] };
    }
    return { processed: [], subGroups: [g] };
  }

  return {
    processed: [],
    subGroups: [
      {
        filters: [],
        groups: subGroups,
        logicalOperator: LogicalOperator.or,
      },
    ],
  };
}
